import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LineChart, Line,
  PieChart, Pie, Cell,
} from "recharts";
import { useApi, Panel, PageHead, Stat, DataTable, StatusBadge, Tabs, inr } from "@/crm/ui";
import EnquiriesDashboardWidget from "@/components/EnquiriesDashboardWidget";

const PERIODS = { Today: "today", "This Month": "month", "This Quarter": "quarter",
  "This Year": "year", "All Time": "all" };
const COLORS = ["#1E3A8A", "#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6", "#14B8A6"];

const CHARTS = [
  ["by_bank", "Bank-wise disbursement", "bank_id"],
  ["by_location", "Location-wise disbursement", "location_id"],
  ["by_employee", "Employee-wise disbursement", "employee_id"],
  ["by_partner", "Partner-wise disbursement", "partner_id"],
  ["by_product", "Product-wise disbursement", "product_id"],
];

export default function ManagementDashboard() {
  const nav = useNavigate();
  const [period, setPeriod] = useState("This Month");
  const d = useApi(`/crm/dashboard/management?period=${PERIODS[period]}`, [period]);
  const data = d.data || {};
  const c = data.cards || {};
  const drill = (report, extra = {}) => {
    const qs = new URLSearchParams(extra).toString();
    nav(`/crm/reports?report=${report}${qs ? `&${qs}` : ""}`);
  };
  const rate = (num, den) => (den ? `${Math.round((num / den) * 100)}%` : "0%");

  return (
    <div className="space-y-6" data-testid="management-dashboard-page">
      <PageHead eyebrow="Management" title="Management dashboard"
                subtitle="Every figure is computed server-side and scoped to your role. Click any card or chart row to drill into the underlying list." />
      <Tabs tabs={Object.keys(PERIODS)} active={period} onChange={setPeriod} testid="period-tabs" />

      <EnquiriesDashboardWidget />

      <div className="grid gap-4 sm:grid-cols-3 xl:grid-cols-6">
        <Stat label="Total Leads" value={c.total_leads ?? "—"} testid="md-leads" onClick={() => drill("lead")} />
        <Stat label="Total Login" value={c.total_login ?? "—"} testid="md-login" onClick={() => drill("login")} />
        <Stat label="Login Amount" value={inr(c.login_amount)} testid="md-login-amount" onClick={() => drill("login")} />
        <Stat label="Approved" value={c.approved ?? "—"} testid="md-approved" onClick={() => drill("approval")} />
        <Stat label="Approved Amount" value={inr(c.approved_amount)} testid="md-approved-amount" onClick={() => drill("approval")} />
        <Stat label="Hold" value={c.hold ?? "—"} testid="md-hold" onClick={() => drill("hold")} />
        <Stat label="Rejected" value={c.rejected ?? "—"} testid="md-rejected" onClick={() => drill("rejection")} />
        <Stat label="Disbursed" value={c.disbursed ?? "—"} testid="md-disbursed" onClick={() => drill("disbursement")} />
        <Stat label="Disbursed Amount" value={inr(c.disbursed_amount)} testid="md-disbursed-amount" onClick={() => drill("disbursement")} />
        <Stat label="Pending Payout" value={inr(c.pending_payout)} testid="md-pending-payout" onClick={() => drill("payout-ageing")} />
        <Stat label="Total Commission" value={inr(c.total_commission)} testid="md-commission" onClick={() => drill("commission")} />
        <Stat label="Total Incentive" value={inr(c.total_incentive)} testid="md-incentive" onClick={() => drill("incentive")} />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <Panel title="Pipeline funnel" testid="md-funnel">
          <ResponsiveContainer width="100%" height={230}>
            <BarChart data={data.funnel} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis type="number" fontSize={11} />
              <YAxis type="category" dataKey="stage" width={104} fontSize={11} />
              <Tooltip />
              <Bar dataKey="count" name="Cases" fill="#1E3A8A" radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title="Monthly trend" testid="md-monthly">
          <ResponsiveContainer width="100%" height={230}>
            <LineChart data={data.monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="month" fontSize={11} />
              <YAxis fontSize={11} tickFormatter={(v) => `${Math.round(v / 100000)}L`} />
              <Tooltip formatter={(v) => inr(v)} /><Legend />
              <Line type="monotone" dataKey="login_amount" name="Login" stroke="#3B82F6" strokeWidth={2} />
              <Line type="monotone" dataKey="disbursed_amount" name="Disbursed" stroke="#10B981" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </Panel>
        <Panel title="Conversion rates" testid="md-rates">
          <div className="grid grid-cols-2 gap-4 text-sm">
            {[["Approval rate", rate(c.approved, c.total_login)],
              ["Rejection rate", rate(c.rejected, c.total_login)],
              ["Disbursement rate", rate(c.disbursed, c.approved)],
              ["Lead → login", rate(c.total_login, c.total_leads)],
              ["Employee commission", inr(c.employee_commission)],
              ["Partner commission", inr(c.partner_commission)]].map(([k, v]) => (
              <div key={k} className="rounded-md border border-border p-3">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{k}</p>
                <p className="num text-lg font-bold">{v}</p>
              </div>))}
          </div>
        </Panel>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {CHARTS.map(([key, title, filterKey]) => (
          <Panel key={key} title={title} testid={`md-${key}`}
                 aside={<span className="text-xs text-muted-foreground">Click a bar to drill down</span>}>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={(data[key] || []).slice(0, 8)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="label" fontSize={10} interval={0} angle={-12} height={44} />
                <YAxis fontSize={11} tickFormatter={(v) => `${Math.round(v / 100000)}L`} />
                <Tooltip formatter={(v) => inr(v)} />
                <Bar dataKey="disbursed_amount" name="Disbursed" radius={[3, 3, 0, 0]}
                     onClick={(bar) => drill("disbursement", { [filterKey]: bar[filterKey] })}>
                  {(data[key] || []).slice(0, 8).map((row, i) => (
                    <Cell key={`${row.label}-${i}`} fill={COLORS[i % COLORS.length]} cursor="pointer" />))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Panel>))}
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <Panel title="Hold ageing" testid="md-hold-ageing">
          <DataTable testid="md-hold-ageing-table" rows={data.hold_ageing} empty="No cases on hold."
                     columns={[{ key: "bucket", label: "Ageing" },
                       { key: "count", label: "Cases", right: true }]} />
        </Panel>
        <Panel title="Payout ageing" testid="md-payout-ageing">
          <DataTable testid="md-payout-ageing-table" rows={data.payout_ageing}
                     empty="No pending payouts."
                     columns={[{ key: "bucket", label: "Ageing" },
                       { key: "count", label: "Payouts", right: true },
                       { key: "amount", label: "Amount", right: true, render: (r) => inr(r.amount) }]} />
        </Panel>
        <Panel title="Payout mix" testid="md-payout-mix">
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={data.payout_summary || []} dataKey="net_payable" nameKey="status"
                   outerRadius={78} label={(e) => e.status}>
                {(data.payout_summary || []).map((row, i) => (
                  <Cell key={`${row.status}-${i}`} fill={COLORS[i % COLORS.length]} />))}
              </Pie>
              <Tooltip formatter={(v) => inr(v)} />
            </PieChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      <Panel title="TAT summary (days)" testid="md-tat">
        <DataTable testid="md-tat-table"
                   rows={Object.entries(data.tat_stages || {}).map(([k, v]) => ({ stage: k.replace(/_/g, " → "), ...v }))}
                   columns={[{ key: "stage", label: "Stage" }, { key: "count", label: "Cases", right: true },
                     { key: "average", label: "Average", right: true },
                     { key: "median", label: "Median", right: true },
                     { key: "max", label: "Maximum", right: true }]} />
      </Panel>

      <Panel title="Bank performance" testid="md-bank-table"
             aside={<StatusBadge value={`Period: ${period}`} />}>
        <DataTable testid="md-bank-performance-table" rows={data.by_bank}
                   onRowClick={(r) => drill("disbursement", { bank_id: r.bank_id })}
                   columns={[{ key: "label", label: "Bank" },
                     { key: "leads", label: "Leads", right: true },
                     { key: "login", label: "Login", right: true },
                     { key: "login_amount", label: "Login Amt", right: true, render: (r) => inr(r.login_amount) },
                     { key: "approved", label: "Approved", right: true },
                     { key: "approved_amount", label: "Approved Amt", right: true, render: (r) => inr(r.approved_amount) },
                     { key: "hold", label: "Hold", right: true },
                     { key: "rejected", label: "Rejected", right: true },
                     { key: "disbursed", label: "Disbursed", right: true },
                     { key: "disbursed_amount", label: "Disbursed Amt", right: true, render: (r) => inr(r.disbursed_amount) },
                     { key: "approval_rate", label: "Approval %", right: true },
                     { key: "average_tat", label: "Avg TAT", right: true }]} />
      </Panel>
    </div>
  );
}
